import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Heart, AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronUp,
  Loader2, FileText, Phone, User, Pill, AlertCircle, X
} from 'lucide-react';
import { toast } from 'sonner';

import { API } from '../config/api';

export default function MedicalQuestionnaire({ childId, childName, compact = false }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    general_health_status: 'good',
    taking_medications: false,
    current_medications: '',
    has_allergies: false,
    food_allergies: '',
    medication_allergies: '',
    environmental_allergies: '',
    other_allergies: '',
    has_asthma: false,
    has_diabetes: false,
    has_epilepsy: false,
    has_heart_condition: false,
    has_joint_problems: false,
    other_conditions: '',
    recent_injuries: '',
    recent_surgeries: '',
    recent_illnesses: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
    doctor_name: '',
    doctor_phone: '',
    additional_notes: ''
  });

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/medical/status/${childId}`);
      setStatus(response.data);
      
      // If we have existing data, pre-fill form
      if (response.data.has_questionnaire) {
        const questResponse = await axios.get(`${API}/medical/questionnaire/${childId}`);
        if (questResponse.data.questionnaire?.data) {
          setFormData(prev => ({
            ...prev,
            ...questResponse.data.questionnaire.data
          }));
        }
      }
    } catch (error) {
      console.error('Failed to load medical status:', error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.emergency_contact_name || !formData.emergency_contact_phone) {
      toast.error('Emergency contact is required');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/medical/questionnaire`, {
        child_id: childId,
        ...formData
      });
      
      toast.success('Medical questionnaire submitted successfully!');
      setShowForm(false);
      loadStatus();
    } catch (error) {
      toast.error('Failed to submit questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getStatusColor = () => {
    if (!status?.has_questionnaire) return 'text-[#ff025f]';
    if (status.is_expired) return 'text-[#ff025f]';
    if (status.days_until_expiry < 30) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getStatusIcon = () => {
    if (!status?.has_questionnaire || status?.is_expired) {
      return <AlertTriangle className="h-4 w-4" />;
    }
    if (status?.days_until_expiry < 30) {
      return <Clock className="h-4 w-4" />;
    }
    return <CheckCircle className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4 flex items-center justify-center">
        <Loader2 className="h-5 w-5 text-[#02b5ff] animate-spin" />
      </div>
    );
  }

  // Compact view for player card
  if (compact && !showForm) {
    return (
      <div className="bg-zinc-800/30 rounded-lg p-3" data-testid="medical-questionnaire-compact">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className={`h-4 w-4 ${getStatusColor()}`} />
            <span className="text-sm text-white font-medium">Medical</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs ${getStatusColor()} flex items-center gap-1`}>
              {getStatusIcon()}
              {!status?.has_questionnaire ? 'Not completed' : 
               status?.is_expired ? 'Expired' :
               status?.days_until_expiry < 30 ? `${status.days_until_expiry}d left` : 'Valid'}
            </span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowForm(true)}
              className="text-[#02b5ff] h-7 px-2"
            >
              {status?.has_questionnaire ? 'Update' : 'Complete'}
            </Button>
          </div>
        </div>
        
        {status?.has_questionnaire && !status?.is_expired && (
          <div className="mt-2 space-y-1">
            {status.has_allergies && status.allergies_summary?.length > 0 && (
              <div className="flex items-start gap-1">
                <AlertCircle className="h-3 w-3 text-[#ff025f] mt-0.5 flex-shrink-0" />
                <span className="text-xs text-zinc-400">
                  Allergies: {status.allergies_summary.join(', ')}
                </span>
              </div>
            )}
            {status.has_conditions && (
              <div className="flex items-start gap-1">
                <Pill className="h-3 w-3 text-yellow-400 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-zinc-400">
                  {status.conditions_summary.join(', ')}
                </span>
              </div>
            )}
            {!status.has_allergies && !status.has_conditions && (
              <span className="text-xs text-green-400">No allergies or conditions reported</span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="medical-questionnaire">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Heart className={`h-5 w-5 ${getStatusColor()}`} />
          <h3 className="text-lg font-bold text-white">Medical Health Questionnaire</h3>
          <span className={`text-xs px-2 py-0.5 rounded-full flex items-center gap-1 ${
            !status?.has_questionnaire || status?.is_expired 
              ? 'bg-[#ff025f]/20 text-[#ff025f]' 
              : status?.days_until_expiry < 30 
                ? 'bg-yellow-500/20 text-yellow-400'
                : 'bg-green-500/20 text-green-400'
          }`}>
            {getStatusIcon()}
            {!status?.has_questionnaire ? 'Required' : 
             status?.is_expired ? 'Renewal Required' :
             `Valid for ${status?.days_until_expiry} days`}
          </span>
        </div>
        {expanded ? <ChevronUp className="h-5 w-5 text-zinc-400" /> : <ChevronDown className="h-5 w-5 text-zinc-400" />}
      </button>

      {expanded && (
        <div className="p-4 border-t border-zinc-800">
          {!showForm && status?.has_questionnaire && !status?.is_expired ? (
            // Display current questionnaire summary
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-zinc-800/30 rounded-lg p-3">
                  <div className="text-xs text-zinc-500 mb-1">General Health</div>
                  <div className="text-sm font-medium text-white capitalize">{status.general_health}</div>
                </div>
                <div className="bg-zinc-800/30 rounded-lg p-3">
                  <div className="text-xs text-zinc-500 mb-1">Allergies</div>
                  <div className={`text-sm font-medium ${status.has_allergies ? 'text-[#ff025f]' : 'text-green-400'}`}>
                    {status.has_allergies ? 'Yes' : 'None'}
                  </div>
                </div>
                <div className="bg-zinc-800/30 rounded-lg p-3">
                  <div className="text-xs text-zinc-500 mb-1">Conditions</div>
                  <div className={`text-sm font-medium ${status.has_conditions ? 'text-yellow-400' : 'text-green-400'}`}>
                    {status.has_conditions ? status.conditions_summary.length : 'None'}
                  </div>
                </div>
                <div className="bg-zinc-800/30 rounded-lg p-3">
                  <div className="text-xs text-zinc-500 mb-1">Emergency Contact</div>
                  <div className="text-sm font-medium text-white truncate">{status.emergency_contact}</div>
                </div>
              </div>

              {status.has_allergies && status.allergies_summary?.length > 0 && (
                <div className="bg-[#ff025f]/10 border border-[#ff025f]/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4 text-[#ff025f]" />
                    <span className="text-sm font-medium text-[#ff025f]">Allergies</span>
                  </div>
                  <ul className="text-sm text-zinc-300 space-y-1">
                    {status.allergies_summary.map((a, i) => (
                      <li key={i}>• {a}</li>
                    ))}
                  </ul>
                </div>
              )}

              {status.has_conditions && status.conditions_summary?.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Pill className="h-4 w-4 text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-400">Medical Conditions</span>
                  </div>
                  <ul className="text-sm text-zinc-300 space-y-1">
                    {status.conditions_summary.map((c, i) => (
                      <li key={i}>• {c}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-between items-center pt-2">
                <span className="text-xs text-zinc-500">
                  Last completed: {new Date(status.completed_at).toLocaleDateString('en-GB')}
                </span>
                <Button
                  onClick={() => setShowForm(true)}
                  variant="outline"
                  className="border-zinc-700"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Update Questionnaire
                </Button>
              </div>
            </div>
          ) : (
            // Questionnaire Form
            <form onSubmit={handleSubmit} className="space-y-6">
              {showForm && (
                <div className="flex justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              
              {/* General Health */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-[#02b5ff] flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  General Health
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-sm">Overall Health Status</Label>
                    <Select value={formData.general_health_status} onValueChange={(v) => updateForm('general_health_status', v)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        <SelectItem value="excellent" className="text-white">Excellent</SelectItem>
                        <SelectItem value="good" className="text-white">Good</SelectItem>
                        <SelectItem value="fair" className="text-white">Fair</SelectItem>
                        <SelectItem value="poor" className="text-white">Poor</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-zinc-400 text-sm">Currently Taking Medications?</Label>
                      <Switch 
                        checked={formData.taking_medications} 
                        onCheckedChange={(v) => updateForm('taking_medications', v)} 
                      />
                    </div>
                    {formData.taking_medications && (
                      <Input
                        value={formData.current_medications}
                        onChange={(e) => updateForm('current_medications', e.target.value)}
                        placeholder="List medications..."
                        className="bg-zinc-800 border-zinc-700 text-white mt-2"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Allergies */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-[#ff025f] flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Allergies
                  </h4>
                  <div className="flex items-center gap-2">
                    <Label className="text-zinc-400 text-sm">Has Allergies?</Label>
                    <Switch 
                      checked={formData.has_allergies} 
                      onCheckedChange={(v) => updateForm('has_allergies', v)} 
                    />
                  </div>
                </div>
                {formData.has_allergies && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-400 text-xs">Food Allergies</Label>
                      <Input
                        value={formData.food_allergies}
                        onChange={(e) => updateForm('food_allergies', e.target.value)}
                        placeholder="e.g., nuts, dairy, gluten..."
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Medication Allergies</Label>
                      <Input
                        value={formData.medication_allergies}
                        onChange={(e) => updateForm('medication_allergies', e.target.value)}
                        placeholder="e.g., penicillin..."
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Environmental Allergies</Label>
                      <Input
                        value={formData.environmental_allergies}
                        onChange={(e) => updateForm('environmental_allergies', e.target.value)}
                        placeholder="e.g., pollen, dust..."
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Other Allergies</Label>
                      <Input
                        value={formData.other_allergies}
                        onChange={(e) => updateForm('other_allergies', e.target.value)}
                        placeholder="Any other allergies..."
                        className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Medical Conditions */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-yellow-400 flex items-center gap-2">
                  <Pill className="h-4 w-4" />
                  Medical Conditions
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: 'has_asthma', label: 'Asthma' },
                    { key: 'has_diabetes', label: 'Diabetes' },
                    { key: 'has_epilepsy', label: 'Epilepsy' },
                    { key: 'has_heart_condition', label: 'Heart Condition' },
                    { key: 'has_joint_problems', label: 'Joint Problems' }
                  ].map(condition => (
                    <div key={condition.key} className="flex items-center justify-between bg-zinc-800/30 rounded p-2">
                      <Label className="text-zinc-300 text-sm">{condition.label}</Label>
                      <Switch 
                        checked={formData[condition.key]} 
                        onCheckedChange={(v) => updateForm(condition.key, v)} 
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Other Conditions</Label>
                  <Input
                    value={formData.other_conditions}
                    onChange={(e) => updateForm('other_conditions', e.target.value)}
                    placeholder="Any other medical conditions..."
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              </div>

              {/* Recent Health Issues */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-zinc-300">Recent Health Issues (past 12 months)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Recent Injuries</Label>
                    <Input
                      value={formData.recent_injuries}
                      onChange={(e) => updateForm('recent_injuries', e.target.value)}
                      placeholder="Any injuries..."
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Recent Surgeries</Label>
                    <Input
                      value={formData.recent_surgeries}
                      onChange={(e) => updateForm('recent_surgeries', e.target.value)}
                      placeholder="Any surgeries..."
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Recent Illnesses</Label>
                    <Input
                      value={formData.recent_illnesses}
                      onChange={(e) => updateForm('recent_illnesses', e.target.value)}
                      placeholder="Any illnesses..."
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-[#02b5ff] flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Emergency Contact <span className="text-[#ff025f]">*</span>
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Contact Name *</Label>
                    <Input
                      value={formData.emergency_contact_name}
                      onChange={(e) => updateForm('emergency_contact_name', e.target.value)}
                      placeholder="Full name"
                      required
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Phone Number *</Label>
                    <Input
                      value={formData.emergency_contact_phone}
                      onChange={(e) => updateForm('emergency_contact_phone', e.target.value)}
                      placeholder="Phone number"
                      required
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Relationship</Label>
                    <Input
                      value={formData.emergency_contact_relationship}
                      onChange={(e) => updateForm('emergency_contact_relationship', e.target.value)}
                      placeholder="e.g., Parent, Guardian"
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Doctor Info */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Doctor Information (Optional)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Doctor Name</Label>
                    <Input
                      value={formData.doctor_name}
                      onChange={(e) => updateForm('doctor_name', e.target.value)}
                      placeholder="Doctor's name"
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Doctor Phone</Label>
                    <Input
                      value={formData.doctor_phone}
                      onChange={(e) => updateForm('doctor_phone', e.target.value)}
                      placeholder="Doctor's phone"
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <Label className="text-zinc-400 text-xs">Additional Notes</Label>
                <Textarea
                  value={formData.additional_notes}
                  onChange={(e) => updateForm('additional_notes', e.target.value)}
                  placeholder="Any other health information we should know..."
                  className="bg-zinc-800 border-zinc-700 text-white mt-1 h-20"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4">
                {showForm && (
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="border-zinc-700">
                    Cancel
                  </Button>
                )}
                <Button type="submit" disabled={submitting} className="bg-[#02b5ff] hover:bg-[#0099dd]">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Submit Questionnaire
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
